import React, { useState, useEffect } from "react";
import { useAsignaturas } from "../../context/AsignaturasContext";
import { useUsers } from "../../context/UsersContext";
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes, FaInfoCircle } from "react-icons/fa";
import ConfirmModal from "../Modals/ConfirmModal";
import AsignacionMasiva from "./AsignacionMasiva";

const AsignacionesSalones = ({ asignaturasConSalones, showNotification }) => {
  const { asignarASalon, actualizarIH, eliminarAsignacion } = useAsignaturas();
  const { salones, fetchSalones } = useUsers();

  const [selectedAsignatura, setSelectedAsignatura] = useState("");
  const [selectedSalon, setSelectedSalon] = useState("");
  const [intensidadHoraria, setIntensidadHoraria] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingIH, setEditingIH] = useState("");
  const [modoAsignacion, setModoAsignacion] = useState("individual");

  useEffect(() => {
    fetchSalones();
  }, [fetchSalones]);

  const getAsignaturaDescripcion = (asignaturaId) => {
    const asignatura = asignaturasConSalones?.find(
      (asig) => asig.asignatura_id === parseInt(asignaturaId)
    );
    return asignatura?.descripcion || null;
  };

  // 🔥 NUEVO: Función para ordenar asignaturas
  const getAsignaturasOrdenadas = () => {
    if (!asignaturasConSalones) return [];

    return [...asignaturasConSalones].sort((a, b) => {
      const aDescripcion = a.descripcion && a.descripcion.trim() !== "";
      const bDescripcion = b.descripcion && b.descripcion.trim() !== "";

      // Las que NO tienen descripción primero
      if (!aDescripcion && bDescripcion) return -1;
      if (aDescripcion && !bDescripcion) return 1;

      // Si ambas tienen o no tienen descripción, ordenar alfabéticamente por nombre
      return a.nombre.localeCompare(b.nombre);
    });
  };

  const selectedAsignaturaDescripcion = getAsignaturaDescripcion(selectedAsignatura);

  const handleAsignar = async (e) => {
    e.preventDefault();

    if (!selectedAsignatura || !selectedSalon) {
      showNotification({
        type: "error",
        message: "Todos los campos son requeridos",
      });
      return;
    }

    try {
      await asignarASalon({
        salon_id: selectedSalon,
        asignatura_id: selectedAsignatura,
        IH: intensidadHoraria || null,
      });
      showNotification({
        type: "success",
        message: "Asignatura asignada correctamente al salón",
      });
      setSelectedAsignatura("");
      setSelectedSalon("");
      setIntensidadHoraria("");
    } catch (error) {
      showNotification({
        type: "error",
        message: error.response?.data?.message || "Error al asignar asignatura",
      });
    }
  };

  const handleActualizarIH = async (asignaturaSalonId) => {
    if (!editingIH || editingIH <= 0) {
      showNotification({
        type: "error",
        message: "La intensidad horaria debe ser mayor a 0",
      });
      return;
    }

    try {
      await actualizarIH(asignaturaSalonId, editingIH);
      showNotification({
        type: "success",
        message: "Intensidad horaria actualizada correctamente",
      });
      setEditingId(null);
      setEditingIH("");
    } catch (error) {
      showNotification({
        type: "error",
        message: "Error al actualizar intensidad horaria",
      });
    }
  };

  const handleEliminar = async () => {
    if (!deleteId) return;

    try {
      await eliminarAsignacion(deleteId);
      showNotification({
        type: "success",
        message: "Asignación eliminada correctamente",
      });
      setDeleteId(null);
    } catch (error) {
      showNotification({
        type: "error",
        message: "Error al eliminar asignación",
      });
    }
  };

  const asignaturasOrdenadas = getAsignaturasOrdenadas();

  return (
    <div className="space-y-6">
      {/* 🔥 NUEVO: Tabs para elegir modo de asignación */}
      <div className="flex gap-2 bg-white p-2 rounded-lg shadow-md">
        <button
          onClick={() => setModoAsignacion("individual")}
          className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
            modoAsignacion === "individual"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          ✏️ Asignación Individual
        </button>
        <button
          onClick={() => setModoAsignacion("masiva")}
          className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
            modoAsignacion === "masiva"
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          ⚡ Asignación Masiva
        </button>
      </div>

      {/* 🔥 NUEVO: Mostrar componente según modo */}
      {modoAsignacion === "masiva" ? (
        <AsignacionMasiva
          asignaturasConSalones={asignaturasOrdenadas}
          showNotification={showNotification}
        />
      ) : (
        <>
          {/* Formulario de asignación individual */}
          <div className="bg-white p-6 rounded-2xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">
              Asignar Asignatura a Salón
            </h3>

            <form onSubmit={handleAsignar} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Asignatura *
                  </label>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedAsignatura}
                      onChange={(e) => setSelectedAsignatura(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Seleccionar asignatura</option>
                      {asignaturasOrdenadas?.map((asig) => (
                        <option key={asig.asignatura_id} value={asig.asignatura_id}>
                          {asig.nombre}
                          {asig.descripcion && asig.descripcion.trim() !== "" && ` - (Dimensión: ${asig.descripcion})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salón *
                  </label>
                  <select
                    value={selectedSalon}
                    onChange={(e) => setSelectedSalon(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Seleccionar salón</option>
                    {salones?.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.grado} - {salon.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Intensidad Horaria (I.H) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={intensidadHoraria}
                    onChange={(e) => setIntensidadHoraria(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: 4"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-green text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                <FaPlus /> Asignar a Salón
              </button>
            </form>
          </div>
        </>
      )}

      {/* Lista de asignaciones por asignatura */}
      <div className="bg-white p-6 rounded-2xl shadow-md">
        <h3 className="text-xl font-semibold mb-4">
          Asignaciones Actuales por Asignatura
        </h3>

        {!asignaturasOrdenadas || asignaturasOrdenadas.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No hay asignaturas registradas
          </p>
        ) : (
          <div className="space-y-6">
            {asignaturasOrdenadas.map((asignatura) => (
              <div
                key={asignatura.asignatura_id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg font-semibold text-blue-900">
                      📚 {asignatura.nombre}
                    </h4>
                    {asignatura.descripcion && (
                      <div className="relative group">
                        <FaInfoCircle className="text-blue-500 cursor-help" size={16} />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg px-3 py-2 w-64 z-10 shadow-lg">
                          Dimensión: {asignatura.descripcion}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    {asignatura.salones_asignados?.length || 0} salones asignados
                  </span>
                </div>

                {!asignatura.salones_asignados || asignatura.salones_asignados.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">
                    Sin salones asignados
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border px-3 py-2 text-left">Salón</th>
                          <th className="border px-3 py-2 text-left">Grado</th>
                          <th className="border px-3 py-2 text-center">I.H</th>
                          <th className="border px-3 py-2 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {asignatura.salones_asignados.map((salon) => (
                          <tr key={salon.asignatura_salon_id} className="hover:bg-gray-50">
                            <td className="border px-3 py-2">{salon.salon_nombre}</td>
                            <td className="border px-3 py-2">{salon.salon_grado}</td>
                            <td className="border px-3 py-2 text-center">
                              {editingId === salon.asignatura_salon_id ? (
                                <input
                                  type="number"
                                  min="1"
                                  value={editingIH}
                                  onChange={(e) => setEditingIH(e.target.value)}
                                  className="w-16 px-2 py-1 border rounded text-center"
                                  autoFocus
                                />
                              ) : (
                                <span className="font-semibold">{salon.IH}</span>
                              )}
                            </td>
                            <td className="border px-3 py-2">
                              <div className="flex justify-center gap-2">
                                {editingId === salon.asignatura_salon_id ? (
                                  <>
                                    <button
                                      onClick={() => handleActualizarIH(salon.asignatura_salon_id)}
                                      className="text-green-600 hover:text-green-800 transition-colors"
                                      title="Guardar"
                                    >
                                      <FaSave size={16} />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingId(null);
                                        setEditingIH("");
                                      }}
                                      className="text-gray-600 hover:text-gray-800 transition-colors"
                                      title="Cancelar"
                                    >
                                      <FaTimes size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => {
                                        setEditingId(salon.asignatura_salon_id);
                                        setEditingIH(salon.IH);
                                      }}
                                      className="text-blue-600 hover:text-blue-800 transition-colors"
                                      title="Editar I.H"
                                    >
                                      <FaEdit size={16} />
                                    </button>
                                    <button
                                      onClick={() => setDeleteId(salon.asignatura_salon_id)}
                                      className="text-red-600 hover:text-red-800 transition-colors"
                                      title="Eliminar asignación"
                                    >
                                      <FaTrash size={16} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {deleteId && (
        <ConfirmModal
          message="¿Estás seguro de eliminar esta asignación? Los estudiantes perderán acceso a esta asignatura en ese salón."
          onConfirm={handleEliminar}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
};

export default AsignacionesSalones;